import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FileText, Calendar, FileSignature } from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import api from '@/lib/axios';
import { getPortalToken } from './portalStorage';
import { formatDate, formatCurrency, formatClocks } from '@/lib/utils';
import { statusLabel } from '@/lib/deLabels';
import PortalTerminationDialog from './PortalTerminationDialog';

export default function PortalContracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [terminating, setTerminating] = useState(null);
  const [pendingTerminationsByContract, setPendingTerminationsByContract] = useState({});

  useEffect(() => {
    fetchContracts();
    fetchTerminations();
  }, []);

  const fetchTerminations = async () => {
    try {
      const r = await api.get('/portal/self-service/terminations');
      const map = {};
      (r.data || []).forEach((t) => {
        if (!map[t.contract_id] || new Date(t.created_at) > new Date(map[t.contract_id].created_at)) {
          map[t.contract_id] = t;
        }
      });
      setPendingTerminationsByContract(map);
    } catch {
      // silent
    }
  };

  const fetchContracts = async () => {
    try {
      const token = getPortalToken();
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await api.get('/portal/contracts');
      setContracts(response.data);
    } catch (error) {
      console.error('Error loading contracts:', error);
      toast.error('Fehler beim Laden der Verträge');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { label: 'Aktiv', className: 'bg-emerald-100 text-emerald-700' },
      pending: { label: 'Ausstehend', className: 'bg-amber-100 text-amber-700' },
      terminated: { label: 'Beendet', className: 'bg-slate-100 text-slate-500' },
      cancelled: { label: 'Storniert', className: 'bg-red-100 text-red-700' },
    };
    const config = variants[status] || variants.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <PortalLayout>
      <div className="space-y-6" data-testid="portal-contracts">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Verträge</h1>
          <p className="text-slate-600">Ihre aktiven Unterrichtsverträge</p>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
              </div>
            ) : contracts.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Keine Verträge gefunden
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {contracts.map((contract) => (
                  <div 
                    key={contract.id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-brand-red/10 rounded-lg">
                          <FileText className="w-5 h-5 text-brand-red" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{contract.student_name}</p>
                          <p className="text-sm text-slate-500">
                            {contract.tariff_name || 'Kein Tarif'}
                            {contract.course_name && ` • ${formatClocks(contract.course_name) || contract.course_name}`}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                            <Calendar className="w-3 h-3" />
                            {contract.start_date ? formatDate(contract.start_date) : 'Kein Start'} 
                            {contract.end_date && ` - ${formatDate(contract.end_date)}`}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-slate-900">{formatCurrency(contract.monthly_fee)}</p>
                          <p className="text-xs text-slate-500">pro Monat</p>
                        </div>
                        {getStatusBadge(contract.status)}
                        {contract.status === 'active' && !pendingTerminationsByContract[contract.id] && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-rose-600 border-rose-200 hover:bg-rose-50"
                            onClick={() => setTerminating(contract)}
                            data-testid={`portal-terminate-btn-${contract.id}`}
                          >
                            <FileSignature className="w-3 h-3 mr-1" /> Kündigen
                          </Button>
                        )}
                        {pendingTerminationsByContract[contract.id] && (
                          <Badge className="bg-amber-100 text-amber-700" data-testid={`portal-termination-pending-${contract.id}`}>
                            Kündigung {statusLabel(pendingTerminationsByContract[contract.id].status)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {terminating && (
        <PortalTerminationDialog
          contract={terminating}
          onClose={() => setTerminating(null)}
          onSubmitted={() => { setTerminating(null); fetchContracts(); fetchTerminations(); }}
        />
      )}
    </PortalLayout>
  );
}
